<template>
  <TolgeeProvider>
    <div>
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>
      <DynamicPopup />
      <DynamicSidePanel />
      <UISnackBanner />
      <SupportOpenAgentInfo />
      <ClientOnly>
        <Websocket />
      </ClientOnly>
    </div>
  </TolgeeProvider>
</template>

<script setup lang="ts">
import * as Sentry from '@sentry/vue'
import { USER_ROLES, USER_STATUSES } from './constants/auth'
import { TolgeeProvider } from '@tolgee/vue'
import { POPUPS } from '@/components/dynamic/maps'

const authStore = useAuthStore()

const { catchErrors } = useErrorHandler()

const { refetch: refetchUser, error: userError } = useCurrentUser()

const { refetch: refetchPackages } = usePackages()

const { refetch: refetchAgent, error: agentError } = useAgent(false)

const { sendEventToBackend } = useTracking()

const uiStore = useUIStore()
const route = useRoute()

const normalizePath = (path: string) => {
  const segments = path.split('/').filter(Boolean)

  if (segments[0]?.length === 2) {
    segments.shift()
  }

  return '/' + segments.join('/')
}

onMounted(() => {
  const { getBclickId } = useBclickId()
  getBclickId()
  
  useInitEverflow()
})

const initUser = async () => {
  await refetchUser()

  if (userError.value) {
    catchErrors(userError.value)
    return
  }
  if (!authStore.getUser) return

  Sentry.setUser({
    id: authStore.getUser.id,
    name: authStore.getUser.firstName,
    email: authStore.getUser.email,
    role: authStore.getUser.role,
  })

  try {
    // Using bracket notation to avoid TypeScript errors since clarity is not defined in Window type
    const clarityFn = (window as any)['clarity']
    if (typeof clarityFn === 'function') {
      clarityFn('identify', authStore.getUser.email)
    }
  } catch (e) {
  }
}

watch(
  () => route.name,
  (_, oldValue) => {
    if (!authStore.getUser || !authStore.getToken) return
    if (oldValue?.toString().includes('login-auto')) {
      initUser()
      refetchAgent()
    }
  }
)

watch(
  () => authStore.getToken,
  async () => {
    if (route.name?.toString().includes('login-auto')) return
    await initUser()
  },
  { immediate: true }
)

watch(
  () => authStore.getUser,
  async (user) => {
    if (route.name?.toString().includes('login-auto')) return
    if (!user) return
    if (user.role !== USER_ROLES.LEAD && user.role !== USER_ROLES.ADMIN) return

    await refetchAgent()
    if (agentError.value) {
      catchErrors(agentError.value)
      return
    }
  },
  { immediate: true, deep: true }
)

const { mutateAsync: updateTimezone } = useUpdateTimezone()

watch(
  () => authStore.getUser,
  async () => {
    if (route.name?.toString().includes('login-auto')) return
    if (!authStore.getUser || !authStore.getToken) return
    // only on client side
    if (typeof window === 'undefined') return

    updateTimezone({
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).catch((error) => {
      catchErrors(error)
    })
  },
  {
    immediate: true,
  }
)

let timeout: ReturnType<typeof setTimeout> | null = null

const { getPaywallForce } = usePaywallParams()

const excludedRoutesRegex = [/^\/payment(\/.*)?$/, /^\/signup(\/.*)?$/, /^\/reset(\/.*)?$/, /^\/login(\/.*)?$/]

const checkAndShowPopup = (user: any) => {
  const currentPath = normalizePath(route.path)
  const isExcluded = excludedRoutesRegex.some((pattern) => pattern.test(currentPath))

  if (isExcluded) {
    if (timeout) {
      clearTimeout(timeout)
      timeout = null
    }
    return
  }

  if (timeout) {
    clearTimeout(timeout)
    timeout = null
  }

  const routePaywallForce = getPaywallForce() === true
  const userPaywallForce = user?.paywallForce === true
  const shouldForcePaywall = routePaywallForce || userPaywallForce

  if (route.query.payment === 'success') {
    return
  }

  if (uiStore.dynamicPopup.value || !user || user?.role !== USER_ROLES.LEAD) {
    return
  }

  const hasPackage = !!(user as any)?.packageId
  const isMember = user?.status === USER_STATUSES.MEMBER
  if (hasPackage || isMember) {
    return
  }

  timeout = setTimeout(() => {
    try {
      if (uiStore.dynamicPopup.value) {
        return
      }
      
      if (shouldForcePaywall) {
        sendEventToBackend('paywall_view', {
          crm_user_id: user.id,
          email: user.email,
          phone: user.phone,
          fname: user.firstName,
          lname: user.lastName,
        })
        uiStore.showPopup(POPUPS.UPGRADE_MODAL)
      } else if (!shouldForcePaywall && !user.phoneVerified) {
        sendEventToBackend('paywall_view', {
          crm_user_id: user.id,
          email: user.email,
          phone: user.phone,
          fname: user.firstName,
          lname: user.lastName,
        })
        uiStore.showPopup(POPUPS.UPGRADE_MODAL)
      } else if (!user.phoneVerified) {
        uiStore.showPopup(POPUPS.VERIFY_PHONE)
      } else if (!user.answeredQuestions) {
        uiStore.showPopup(POPUPS.FILL_PROFILE)
      } else if (!user.bookedFirstCall) {
        uiStore.showPopup(POPUPS.BOOK_A_CALL)
      } else if (user.role === USER_ROLES.LEAD) {
        sendEventToBackend('paywall_view', {
          crm_user_id: user.id,
          email: user.email,
          phone: user.phone,
          fname: user.firstName,
          lname: user.lastName,
        })
        uiStore.showPopup(POPUPS.UPGRADE_MODAL)
      }
    } catch (error) {
      console.error('Error showing popup:', error)
    }
  }, 7000)
}

// Watch for user changes
watch(
  () => authStore.user,
  (user) => {
    checkAndShowPopup(user)
  },
  {
    immediate: true,
    deep: true,
  }
)

// Watch for route changes
watch(
  () => route.path,
  () => {
    if (authStore.user) {
      checkAndShowPopup(authStore.user)
    }
  }
)

let paymentSuccessHandled = false

watch(
  () => route.query.payment,
  async (payment) => {
    if (payment === 'success' && !paymentSuccessHandled) {
      paymentSuccessHandled = true
      await Promise.all([refetchUser(), refetchPackages()])
      nextTick(() => {
        const gtm = useGtm()
        gtm?.trackEvent({ event: 'purchase' })
        uiStore.showPopup(POPUPS.PAYMENT_SUCCESS)
      })
    } else if (payment !== 'success') {
      paymentSuccessHandled = false
    }
  },
  { immediate: true }
)

// Reset the flag when dynamic popup is closed
watch(
  () => uiStore.dynamicPopup.value,
  (isShowing) => {
    if (!isShowing && timeout) {
      clearTimeout(timeout)
      timeout = null
    }
    if (!isShowing) {
      paymentSuccessHandled = false
    }
  }
)

onUnmounted(() => {
  if (timeout) {
    clearTimeout(timeout)
    timeout = null
  }
})
</script>
